import React, { useCallback, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { DA_Select } from "@danishagro/shared/src/components/atoms/Select/Select.component";
import { DA_Spinner } from "@danishagro/shared/src/components/atoms/Spinner/Spinner.component";
import { useUserRolesApi } from "@danishagro/sharedShop/src/hooks/api/useUserRolesApi.hook";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import {
    DA_CheckboxList,
    DA_CheckboxType,
} from "@danishagro/shared/src/components/molecules/CheckboxList/CheckboxList.component";
import { DA_InputWrapper } from "@danishagro/shared/src/components/atoms/InputWrapper/InputWrapper.component";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useBottomSheet } from "@danishagro/shared/src/contexts/bottomSheet/bottomSheet.context";
import S from "./EditUserBottomSheet.module.scss";

type SelectOption = {
    value: string;
    label: string;
};

interface EditUserBottomSheetProps {
    id: string;
    name: string;
    cvr: string;
    role: string;
    onSubmit: () => void;
}

export const _EditUserBottomSheet = ({ id, onSubmit }: EditUserBottomSheetProps) => {
    const { getDictionaryString, getDictionaryItem } = useTranslations();
    const { getUser, editUserV2 } = useUserRolesApi();
    const { customerNumber } = useAppData();
    const [accounts, setAccounts] = useState<DA_CheckboxType[]>([]);
    const [possibleRoles, setPossibleRoles] = useState<SelectOption[]>([]);
    const [role, setRole] = useState<string>();
    const [loading, setLoading] = useState(true);
    const [isValid, setIsValid] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { dismissBottomSheet } = useBottomSheet();

    const submit = useCallback(() => {
        setSubmitting(true);
        editUserV2(id, {
            roleAlias: role,
            customerNumbers: accounts.map((account) => ({
                customerNumber: account.value,
                active: account.checked ? true : false,
            })),
        })
            .then(() => {
                dismissBottomSheet();
                onSubmit();
            })
            .finally(() => {
                setTimeout(() => {
                    setSubmitting(false);
                }, 500);
            });
    }, [editUserV2, id, role, accounts, dismissBottomSheet, onSubmit]);

    const getUserInfo = useCallback(async () => {
        getUser(id, customerNumber)
            .then((response) => {
                setPossibleRoles(
                    response.possibleRoles.map((value) => ({
                        label: getDictionaryString(value, {
                            uppercaseFirst: true,
                        }),
                        value,
                    }))
                );
                setRole(response.userRoles[0]?.roleAlias);
                setAccounts(
                    response.customerNumbersUserCanEdit.map(({ name, number }) => ({
                        name: `${number} - ${name}`,
                        label: `${number} - ${name}`,
                        value: number,
                        checked: !!response.userRoles.find(
                            ({ customerNumber }) => customerNumber === number
                        ),
                    }))
                );
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [customerNumber, getDictionaryString, id, getUser]);

    useEffect(() => {
        getUserInfo();
    }, [getUserInfo]);

    useEffect(() => {
        const selectedAccounts = accounts.filter((item) => item.checked);

        if (selectedAccounts.length) {
            setIsValid(true);
        } else {
            setIsValid(false);
        }
    }, [accounts]);

    return (
        <>
            <div className={S.selectGroup}>
                <DA_InputWrapper
                    label={getDictionaryItem("userType", { uppercaseFirst: true })}
                    forId="edit-user-type"
                >
                    {loading ? (
                        <Skeleton width="100%" />
                    ) : (
                        <DA_Select
                            id="edit-user-type"
                            value={role}
                            onChange={(newType) => setRole(newType.value)}
                            options={possibleRoles}
                            disabled={!possibleRoles.length}
                            fullWidth
                        />
                    )}
                </DA_InputWrapper>
            </div>

            <div className={S.checkboxListWrapper}>
                <DA_CheckboxList
                    className={S.checkboxList}
                    isLoading={loading}
                    checkboxList={accounts}
                    onChange={(newAccounts) => setAccounts(newAccounts)}
                />
            </div>

            {!submitting && (
                <div className={S.buttonWrapper}>
                    <DA_Button
                        title={getDictionaryString("cancel")}
                        isGhost
                        disabled={loading}
                        onClick={dismissBottomSheet}
                        className={S.secondary}
                    />
                    <DA_Button
                        title={getDictionaryString("save")}
                        onClick={submit}
                        className={S.primary}
                        disabled={loading || submitting || !isValid}
                    />
                </div>
            )}

            {submitting && <DA_Spinner className={S.spinner} />}
        </>
    );
};
